import { errorLogger } from "@/Common/helpers/logger";

const hasOddNumberOfQuotes = (text: string) => {
	const quoteCount = (text.match(/"/g) ?? []).length;
	return quoteCount % 2 !== 0;
};

const mergeUnfinishedParts = (parts: string[]) => {
	const mergedParts = [];
	let index = 0;
	while (index < parts.length) {
		const currentPart = parts[index];
		const nextPart = parts[index + 1];

		if (hasOddNumberOfQuotes(currentPart) && !currentPart.endsWith("]") && /^\s*$/.test(nextPart)) {
			// has an unclosed quote
			mergedParts.push(currentPart + nextPart);
			index++;
		} else {
			mergedParts.push(currentPart);
		}
		index++;
	}
	return mergedParts;
};

const mergeUnfinishedStringParts = (parts: string[]) => {
	const mergedParts: string[] = [];
	let mergedPartsIndex = 0;
	let index = 0;
	while (index < parts.length) {
		const currentPart = parts[index];
		const lastPart = parts[index - 1];

		if (currentPart.startsWith('"') && lastPart?.endsWith('"')) {
			// it's a single string that was escaped
			mergedPartsIndex--;
			mergedParts[mergedPartsIndex] = (mergedParts[mergedPartsIndex] ?? "") + currentPart;
		} else {
			mergedParts[mergedPartsIndex] = currentPart;
		}
		index++;
		mergedPartsIndex++;
	}
	return mergedParts;
};

export interface ICursorInfo extends IKeysAndValuesResult {
	replaceStart: number;
	replaceEnd: number;
	isBetweenBlocks: boolean;
	autocompleteType: "key" | "value";
	currentValueString?: string;
}

const getCurrentPart = (text: string, cursorPosition: number) => {
	const parts = splitTextOutsideBrackets(text);

	let totalLengthBefore = 0;

	const currentPart = parts.find((part) => {
		if (totalLengthBefore + part.length >= cursorPosition) {
			return true;
		}
		totalLengthBefore += part.length;
		return false;
	});

	return { currentPart, totalLengthBefore };
};

const checkIsOutsideBrackets = (analisisResult: IKeysAndValuesResult, indexInPart: number) => {
	const isCompleted = [
		analisisResult.hasOpenBracket,
		analisisResult.hasCloseBracket,
		analisisResult.hasCompleteKey,
		analisisResult.hasCompleteValue,
		analisisResult.hasColon,
	].every(Boolean);

	if (isCompleted && indexInPart === 0) {
		return {
			isOutsideBrackets: true,
			offset: 0,
		};
	}

	if (isCompleted && indexInPart === analisisResult.queryBlock.length) {
		return {
			isOutsideBrackets: true,
			offset: analisisResult.queryBlock.length,
		};
	}

	return {
		isOutsideBrackets: false,
		offset: 0,
	};
};

export const findCursorInfo = (cursorPosition: number, currentString = ""): ICursorInfo => {
	const { currentPart, totalLengthBefore } = getCurrentPart(currentString, cursorPosition);

	if (!currentPart) {
		return {
			replaceStart: 0,
			replaceEnd: 0,
			isBetweenBlocks: false,
			autocompleteType: "key",
			...parseKeysAndValues(" "),
		};
	}

	const analisisResult = parseKeysAndValues(currentPart);
	const indexInPart = cursorPosition - totalLengthBefore;

	if (currentPart === " ") {
		// in the middle of two query blocks, should insert a new query block
		return {
			replaceStart: totalLengthBefore,
			replaceEnd: totalLengthBefore + currentPart.length,
			isBetweenBlocks: true,
			autocompleteType: "key",
			...analisisResult,
		};
	}

	if (/(^[a-zA-Z[&!~|,()-]+$)/.test(currentPart)) {
		// in the middle of an operator/parentesis, should insert a new query block at the end
		return {
			replaceStart: totalLengthBefore + currentPart.length,
			replaceEnd: totalLengthBefore + currentPart.length,
			isBetweenBlocks: true,
			autocompleteType: "key",
			...analisisResult,
		};
	}

	const { isOutsideBrackets, offset } = checkIsOutsideBrackets(analisisResult, indexInPart);

	if (isOutsideBrackets) {
		// at the start/end of a completed query block
		return {
			replaceStart: totalLengthBefore + offset,
			replaceEnd: totalLengthBefore + offset,
			isBetweenBlocks: true,
			autocompleteType: "key",
			...parseKeysAndValues(" "),
		};
	}

	if (!analisisResult.hasColon) {
		return {
			replaceStart: totalLengthBefore + (analisisResult.hasOpenBracket ? 1 : 0),
			replaceEnd: totalLengthBefore + currentPart.length,
			isBetweenBlocks: false,
			autocompleteType: "key",
			...analisisResult,
		};
	}

	const { start, end } = getQuotedStringPositions(analisisResult, indexInPart);

	return {
		replaceStart: totalLengthBefore + start,
		replaceEnd: totalLengthBefore + end,
		isBetweenBlocks: false,
		autocompleteType: analisisResult.hasColon && indexInPart > analisisResult.colonIndex ? "value" : "key",
		...analisisResult,
		currentValueString: currentPart.slice(start, end),
	};
};

export function splitTextOutsideBrackets(text: string) {
	const regex = /(\[[^[\]]*\]?)|(\s+)/g;
	const result: string[] = [];
	let match;
	let lastIndex = 0;

	while ((match = regex.exec(text)) !== null) {
		if (match.index > lastIndex) {
			result.push(text.slice(lastIndex, match.index));
		}
		if (match[1]) {
			result.push(match[1]);
		} else if (match[2]) {
			result.push(match[2]);
		}
		lastIndex = regex.lastIndex;
	}

	if (lastIndex < text.length) {
		result.push(text.slice(lastIndex));
	}

	return mergeUnfinishedParts(result);
}

export function getQuotedStringPositions(analisisResult: IKeysAndValuesResult, indexInBlock: number) {
	const { keyPart, currentValues, valuesIndex } = analisisResult;

	if (indexInBlock <= keyPart.length) {
		return {
			start: keyPart.indexOf('"'),
			end: keyPart.lastIndexOf('"') + 1,
		};
	}

	const stringParts = currentValues.split(/("[^"]*"?)|(\s)/g).filter(Boolean);
	const parts = mergeUnfinishedStringParts(stringParts);

	let lengthBeforeValues = valuesIndex;

	if (!parts.length) {
		return {
			start: lengthBeforeValues,
			end: lengthBeforeValues,
		};
	}

	const currentPart = parts.find((part, currentIndex, allParts) => {
		const nextHasQuotes = allParts[currentIndex + 1]?.startsWith('"');

		if (lengthBeforeValues + part.length >= indexInBlock && (part.startsWith('"') || !nextHasQuotes)) {
			return true;
		}
		lengthBeforeValues += part.length;
		return false;
	});

	if (!currentPart) {
		return {
			start: lengthBeforeValues,
			end: lengthBeforeValues,
		};
	}

	if (/^\s+$/.test(currentPart)) {
		return {
			start: lengthBeforeValues + currentPart.length,
			end: lengthBeforeValues + currentPart.length,
		};
	}

	return {
		start: lengthBeforeValues,
		end: lengthBeforeValues + currentPart.trimEnd().length,
	};
}

export const splitBracketBlock = (queryBlock: string) => {
	const [keyPart, colon = "", ...valuesParts] = queryBlock.split(/(:)/);
	const valuesPart = valuesParts?.join("") ?? "";
	return {
		keyPart,
		colon,
		valuesPart,
		colonIndex: colon ? keyPart.length : -1,
		valuesIndex: colon ? keyPart.length + 1 : -1,
	};
};

const removeBrackets = (text: string) => text.replace(/(^\[|\]$)/g, "");

interface IKeysAndValuesResult {
	queryBlock: string;
	hasOpenBracket: boolean;
	hasCloseBracket: boolean;
	hasCompleteKey: boolean;
	hasCompleteValue: boolean;
	hasColon: boolean;
	colonIndex: number;
	valuesIndex: number;
	currentValues: string;
	currentKey: string;
	keyPart: string;
	valuesPart: string;
}

const parseKeysAndValues = (queryBlock: string): IKeysAndValuesResult => {
	const { keyPart, colon, colonIndex, valuesPart, valuesIndex } = splitBracketBlock(queryBlock);

	const hasOpenBracket = keyPart.startsWith("[");
	const hasCloseBracket = valuesPart.endsWith("]");

	const currentKey = removeBrackets(keyPart);
	const currentValues = removeBrackets(valuesPart);

	const hasCompleteKey = hasOpenBracket && /^\s*"([^"])*"\s*$/.test(currentKey);
	const hasCompleteValue = hasOpenBracket && hasCloseBracket && !hasOddNumberOfQuotes(currentValues) && Boolean(currentValues.length);

	const hasColon = colon === ":";

	return {
		queryBlock,
		hasOpenBracket,
		hasCloseBracket,
		hasCompleteKey,
		hasCompleteValue,
		hasColon,
		colonIndex,
		valuesIndex,
		currentValues,
		keyPart,
		valuesPart,
		currentKey: hasOpenBracket ? currentKey : "",
	};
};

export const normalizeCamelCase = (str: string) => {
	try {
		let words = str?.split(/(?=[A-Z])/);
		words = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

		return words?.join(" ") || str || "";
	} catch (err) {
		errorLogger(err as Error);
		return "Keys";
	}
};

export const getCleanKey = (value: string) => {
	const pattern = /(^\s*"|"\s*$)/g;
	return value.replaceAll(pattern, "");
};

export const getCleanValue = (value?: string) => {
	if (!value || ['""', '"'].includes(value)) {
		return { value: "", hasOpenQuote: value === '"', hasCloseQuote: value === '""' };
	}

	let hasOpenQuote = false;
	let hasCloseQuote = false;
	const parts = value.split('""').map((part, index, allParts) => {
		let cleanedPart = part;
		if (index === 0 && part.startsWith('"')) {
			hasOpenQuote = true;
			cleanedPart = cleanedPart.slice(1);
		}
		if (index === allParts.length - 1 && part.endsWith('"')) {
			hasCloseQuote = true;
			cleanedPart = cleanedPart.slice(0, -1);
		}
		return cleanedPart;
	});

	return { value: parts.join('"'), hasOpenQuote, hasCloseQuote };
};

export const validateCriteria = (criteriaValue: string) => {
	if (!criteriaValue.trim().endsWith("]") && !criteriaValue.trim().endsWith(")")) {
		throw new Error("Criteria must end with a closing brackets");
	}

	const stack = [];
	const openingBrackets = ["[", "("];
	const closingBrackets = ["]", ")"];
	const matchingSymbols = ["[]", "()"];

	let insideQuotes = false;

	for (const char of criteriaValue) {
		if (char === '"') {
			insideQuotes = !insideQuotes;
		}
		if (!insideQuotes && openingBrackets.includes(char)) {
			stack.push(char);
		}
		if (!insideQuotes && closingBrackets.includes(char)) {
			const lastOpening = stack.pop();
			if (!matchingSymbols.includes(lastOpening + char)) {
				throw new Error("Brackets are not balanced");
			}
		}
	}
	if (stack.length) {
		throw new Error("Brackets are not balanced");
	}
};
